import type { QuizAttempt } from '../types/quiz'

import { Button } from '@/shared/ui/Button'
import { AppLink } from '@/shared/lib/router'

interface QuizAttemptResultProps {
  attempt: QuizAttempt
}

export function QuizAttemptResult({ attempt }: QuizAttemptResultProps) {
  const questions = attempt.quiz_attempt_questions ?? []
  // O gabarito só vem quando o quiz permite revisão — o serializer de resultado
  // é outra classe, então a ausência do campo é a resposta.
  const hasReview = questions.some(
    question => question.correct_option_id !== undefined
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl bg-white/70 p-4 text-center">
        <p className="text-3xl font-black text-jacaranda tabular-nums">
          {attempt.correct_count} / {attempt.total_count}
        </p>
        <p className="text-sm font-semibold text-[#7F7FAD]">
          {attempt.score_percentage ?? 0}% de acerto
        </p>
        {attempt.passed !== null && (
          <p
            className={
              attempt.passed
                ? 'mt-2 text-sm font-bold text-[#2E7D53]'
                : 'mt-2 text-sm font-bold text-[#B23B41]'
            }
          >
            {attempt.passed ? 'Aprovado' : 'Não atingiu a nota de corte'}
          </p>
        )}
        {attempt.status === 'expired' && (
          <p className="mt-2 text-sm font-semibold text-[#B23B41]">
            O tempo acabou — as perguntas não respondidas contaram como erro.
          </p>
        )}
        <p className="mt-3 text-sm font-bold text-jacaranda">
          {attempt.points_awarded} pontos
          {attempt.coins_awarded > 0
            ? ` · ${attempt.coins_awarded} moedas`
            : ''}
        </p>
        {/* O quiz vale uma pontuação só: esta substituiu a anterior, se havia. */}
        <p className="mt-2 text-xs font-semibold text-[#7F7FAD]">
          É esta a pontuação que vale para este quiz. Refazer substitui por
          uma nova.
        </p>
      </div>

      {hasReview && (
        <div className="flex flex-col gap-3">
          {questions.map((question, index) => {
            const chosen = question.quiz_question_options.find(
              option => option.id === question.selected_option_id
            )
            const right = question.quiz_question_options.find(
              option => option.id === question.correct_option_id
            )

            return (
              <div
                key={question.id}
                className="rounded-xl bg-white/70 p-4 text-left"
              >
                <p className="text-sm font-bold text-jacaranda">
                  {index + 1}. {question.statement}
                </p>
                <p
                  className={
                    question.correct
                      ? 'mt-2 text-sm font-semibold text-[#2E7D53]'
                      : 'mt-2 text-sm font-semibold text-[#B23B41]'
                  }
                >
                  Sua resposta: {chosen?.name ?? 'não respondida'}
                </p>
                {!question.correct && right && (
                  <p className="text-sm font-semibold text-[#2E7D53]">
                    Resposta correta: {right.name}
                  </p>
                )}
                {question.explanation && (
                  <p className="mt-2 text-sm text-[#7F7FAD]">
                    {question.explanation}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}

      <Button as={AppLink} href="/quizzes" variant="outline" className="mx-auto">
        Voltar aos quizzes
      </Button>
    </div>
  )
}
